import React, { useState, useEffect, useRef } from 'react';
import './ActiveCall.css';

function ActiveCall({ callerInfo, audioUrls, onCallEnd }) {
  const [callDuration, setCallDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const audioRefs = useRef([]);
  const timerRef = useRef(null);
  const isInitialized = useRef(false);

  // Defensive check for null audioUrls
  if (!audioUrls) {
    console.error('ActiveCall: audioUrls is null');
    return (
      <div className="active-call">
        <div className="call-header">
          <h2>Error</h2>
          <p>Audio not available</p>
        </div>
      </div>
    );
  }

  const segments = [
    audioUrls.greeting,
    audioUrls.mainMessage,
    audioUrls.closing
  ].filter(Boolean); // Filter out any null/undefined segments

  useEffect(() => {
    // Prevent double initialization (React StrictMode in dev)
    if (isInitialized.current) return;
    isInitialized.current = true;

    // Start playing the first audio segment
    playSegment(0);

    // Start timer
    timerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(timerRef.current);
      audioRefs.current.forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
    };
  }, []);

  const playSegment = (index) => {
    if (index >= segments.length) {
      // All segments played, end call
      setIsPlaying(false);
      isInitialized.current = false; // Prevent any further audio
      setTimeout(() => onCallEnd(), 1000);
      return;
    }

    setCurrentSegment(index);
    setIsPlaying(true);

    const audio = new Audio(segments[index]);
    audioRefs.current[index] = audio;

    // Apply current volume and mute settings
    audio.volume = isMuted ? 0 : volume;

    audio.onended = () => {
      // Check if component is still mounted before playing next segment
      if (isInitialized.current) {
        playSegment(index + 1);
      }
    };

    audio.onerror = (e) => {
      console.error('Error playing audio:', e);
      if (isInitialized.current) {
        playSegment(index + 1);
      }
    };

    audio.play().catch(err => {
      console.error('Playback error:', err);
      if (isInitialized.current) {
        playSegment(index + 1);
      }
    });
  };

  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);

    // Update all active audio elements
    audioRefs.current.forEach(audio => {
      if (audio) {
        audio.volume = newMutedState ? 0 : volume;
      }
    });
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);

    // If adjusting volume, unmute
    if (isMuted && newVolume > 0) {
      setIsMuted(false);
    }

    // Update all active audio elements
    audioRefs.current.forEach(audio => {
      if (audio) {
        audio.volume = newVolume;
      }
    });
  };

  const handleEndCall = () => {
    // Stop all audio immediately
    setIsPlaying(false);
    isInitialized.current = false; // Prevent any new audio from starting

    audioRefs.current.forEach(audio => {
      if (audio) {
        audio.pause();
        audio.src = ''; // Clear the source to stop loading
      }
    });

    // Call parent's end handler
    onCallEnd();
  };

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return '🔇';
    if (volume < 0.5) return '🔉';
    return '🔊';
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getCallerName = () => {
    switch(callerInfo.callerType) {
      case 'SANTA':
        return 'Santa';
      case 'ELF':
      case 'ELF_MALE':
        return 'Jingle Jack';
      case 'ELF_FEMALE':
        return 'Snowdrop Ellie';
      case 'MRS_CLAUS':
        return 'Mrs. Claus';
      case 'GRANDPA_JOE':
        return 'Santa';
      default:
        return 'Santa';
    }
  };

  const getCallerImage = () => {
    switch(callerInfo.callerType) {
      case 'SANTA':
        return '/Images/Santa_Calling.png';
      case 'ELF':
      case 'ELF_MALE':
      case 'ELF_FEMALE':
        return '/Images/Elf_Calling.png';
      case 'MRS_CLAUS':
        return '/Images/MrsClaus_Calling.png';
      case 'GRANDPA_JOE':
        return '/Images/GrandpaJoe_Calling.png';
      default:
        return '/Images/Santa_Calling.png';
    }
  };

  return (
    <div className="active-call">
      <div className="call-header">
        <h2 className="caller-name-active">{getCallerName()}</h2>
        <p className="call-timer">{formatDuration(callDuration)}</p>
      </div>

      <div className="caller-display">
        <img
          src={getCallerImage()}
          alt={getCallerName()}
          className="caller-image-active"
        />
        {isPlaying && (
          <div className="audio-indicator">
            <div className="wave"></div>
            <div className="wave"></div>
            <div className="wave"></div>
          </div>
        )}
      </div>

      <div className="call-controls">
        <button
          className={`control-btn ${isMuted ? 'muted' : ''}`}
          onClick={toggleMute}
        >
          <span className="control-icon">{isMuted ? '🔇' : '🎤'}</span>
          <span className="control-label">{isMuted ? 'unmute' : 'mute'}</span>
        </button>

        <div className="volume-control-container">
          <button
            className="control-btn"
            onClick={() => setShowVolumeSlider(!showVolumeSlider)}
          >
            <span className="control-icon">{getVolumeIcon()}</span>
            <span className="control-label">speaker</span>
          </button>
          {showVolumeSlider && (
            <div className="volume-slider-container">
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="volume-slider"
              />
            </div>
          )}
        </div>

        <button className="control-btn end-call-btn" onClick={handleEndCall}>
          <span className="control-icon">📞</span>
        </button>
      </div>
    </div>
  );
}

export default ActiveCall;
